import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, ArrowDownCircle, ArrowUpCircle, History, Search, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { NFImportDialog } from "@/components/NFImportDialog";

const defaultCategories = [
  { value: "tubo", label: "Tubo" }, { value: "cano", label: "Cano" },
  { value: "conexao", label: "Conexao" }, { value: "perfil", label: "Perfil" },
  { value: "fita", label: "Fita" }, { value: "outro", label: "Outro" },
];

const defaultUnits = [
  { value: "metro", label: "Metro" }, { value: "unidade", label: "Unidade" },
  { value: "kg", label: "Kg" }, { value: "rolo", label: "Rolo" },
  { value: "peca", label: "Peca" },
];

const emptyForm = { name: "", code: "", category: "outro", unit: "unidade", quantity: 0, total_quantity: 0, min_stock: 0, client_id: "", client_name: "", obra: "" };
const emptyMovForm = { type: "entrada", quantity: 0, obra: "", nf: "", notes: "" };

export default function InventoryPage() {
  const { can } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matDialog, setMatDialog] = useState(false);
  const [movDialog, setMovDialog] = useState(false);
  const [histDialog, setHistDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedMat, setSelectedMat] = useState(null);
  const [movements, setMovements] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [movForm, setMovForm] = useState(emptyMovForm);
  const [categories, setCategories] = useState(defaultCategories);
  const [units, setUnits] = useState(defaultUnits);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClientId, setFilterClientId] = useState("all");
  const [nfImportOpen, setNfImportOpen] = useState(false);

  const fetchMaterials = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/materials`, { withCredentials: true });
      setMaterials(data);
    } catch { toast.error("Erro ao carregar materiais"); }
    finally { setLoading(false); }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch (err) { console.error("fetchClients:", err); }
  }, []);

  useEffect(() => {
    fetchMaterials(); fetchClients();
    axios.get(`${API}/settings/categories`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setCategories(data.items); })
      .catch((err) => console.error("fetchCategories:", err));
    axios.get(`${API}/settings/units`, { withCredentials: true })
      .then(({ data }) => { if (data.items?.length) setUnits(data.items); })
      .catch((err) => console.error("fetchUnits:", err));
  }, [fetchMaterials, fetchClients]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setMatDialog(true);
  };

  const openEdit = (mat) => {
    setEditing(mat);
    setForm({
      name: mat.name, code: mat.code || "", category: mat.category, unit: mat.unit,
      quantity: mat.quantity, total_quantity: mat.total_quantity || 0, min_stock: mat.min_stock,
      client_id: mat.client_id || "", client_name: mat.client_name || "",
      obra: mat.obra || ""
    });
    setMatDialog(true);
  };

  const handleDuplicate = (mat) => {
    setEditing(null);
    setForm({
      name: mat.name, code: mat.code ? mat.code + " (copia)" : "", category: mat.category, unit: mat.unit,
      quantity: 0, total_quantity: mat.total_quantity || 0, min_stock: mat.min_stock,
      client_id: mat.client_id || "", client_name: mat.client_name || "",
      obra: mat.obra || ""
    });
    setMatDialog(true);
    toast.info("Material duplicado - altere os dados e salve");
  };

  const openMovement = (mat, type) => {
    setSelectedMat(mat);
    setMovForm({ type, quantity: 0, obra: mat.obra || "", nf: "", notes: "" });
    setMovDialog(true);
  };

  const openHistory = async (mat) => {
    setSelectedMat(mat);
    try {
      const { data } = await axios.get(`${API}/materials/${mat.id}/movements`, { withCredentials: true });
      setMovements(data);
      setHistDialog(true);
    } catch { toast.error("Erro ao carregar historico"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/materials/${editing.id}`, form, { withCredentials: true });
        toast.success("Material atualizado");
      } else {
        await axios.post(`${API}/materials`, form, { withCredentials: true });
        toast.success("Material cadastrado");
      }
      setMatDialog(false);
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/materials/${selectedMat.id}/movement`, movForm, { withCredentials: true });
      toast.success(movForm.type === "entrada" ? "Entrada registrada" : "Saida registrada");
      setMovDialog(false);
      fetchMaterials();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao registrar movimentacao"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusao?")) return;
    try {
      await axios.delete(`${API}/materials/${id}`, { withCredentials: true });
      toast.success("Material removido");
      fetchMaterials();
    } catch { toast.error("Erro ao remover"); }
  };

  const handleClientChange = (clientId) => {
    if (clientId === "_none") {
      setForm({ ...form, client_id: "", client_name: "" });
    } else {
      const cl = clients.find((c) => c.id === clientId);
      setForm({ ...form, client_id: clientId, client_name: cl?.name || "" });
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Estoque</h1>
          <p className="text-sm text-muted-foreground mt-1">Controle de materias-primas</p>
        </div>
        <div className="flex gap-2">
          {can("estoque", "create") && (
          <Button variant="outline" onClick={() => setNfImportOpen(true)} data-testid="import-nf-btn">
            <Sparkles className="h-4 w-4 mr-2" /> Importar NF
          </Button>
          )}
          {can("estoque", "create") && (
          <Button onClick={openCreate} data-testid="create-material-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo Material
          </Button>
          )}
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por codigo ou nome do material..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="inventory-search-input"
          />
        </div>
        <Select value={filterClientId} onValueChange={setFilterClientId}>
          <SelectTrigger className="w-56" data-testid="inventory-client-filter"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os clientes</SelectItem>
            <SelectItem value="none">Sem cliente</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-border shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codigo</TableHead>
                <TableHead>Material</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Obra</TableHead>
                <TableHead>Qtd. Recebida</TableHead>
                <TableHead>Qtd. Utilizada</TableHead>
                <TableHead>Disponivel</TableHead>
                <TableHead>Qtd. Total</TableHead>
                <TableHead>Falta Receber</TableHead>
                <TableHead>Est. Min.</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px]">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : (() => {
                const term = searchTerm.toLowerCase().trim();
                let filtered = materials;
                if (term) {
                  filtered = filtered.filter((m) =>
                    (m.code || "").toLowerCase().includes(term) || (m.name || "").toLowerCase().includes(term)
                  );
                }
                if (filterClientId === "none") {
                  filtered = filtered.filter((m) => !m.client_id);
                } else if (filterClientId !== "all") {
                  filtered = filtered.filter((m) => m.client_id === filterClientId);
                }
                return filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                    {term ? "Nenhum material encontrado para essa busca" : "Nenhum material cadastrado"}
                  </TableCell></TableRow>
                ) : (
                  filtered.map((m) => {
                  const usedQty = m.used_quantity || 0;
                  const availableQty = (m.quantity || 0) - usedQty;
                  const isLow = availableQty <= m.min_stock;
                  const totalQty = m.total_quantity || 0;
                  const remaining = totalQty - (m.quantity || 0);
                  return (
                    <TableRow key={m.id} data-testid={`material-row-${m.id}`}>
                      <TableCell className="font-mono text-xs">{m.code || "-"}</TableCell>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="capitalize text-sm">{m.category}</TableCell>
                      <TableCell className="text-sm">{m.client_name || "-"}</TableCell>
                      <TableCell className="text-sm">{m.obra || "-"}</TableCell>
                      <TableCell className="text-sm" data-testid={`material-received-${m.id}`}>{m.quantity} {m.unit}</TableCell>
                      <TableCell className="text-sm" data-testid={`material-used-${m.id}`}>{usedQty} {m.unit}</TableCell>
                      <TableCell className="text-sm" data-testid={`material-available-${m.id}`}>
                        <span className={availableQty <= m.min_stock ? "text-red-600 font-medium" : "text-foreground"}>
                          {availableQty} {m.unit}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`material-total-${m.id}`}>{totalQty ? `${totalQty} ${m.unit}` : "-"}</TableCell>
                      <TableCell className="text-sm" data-testid={`material-remaining-${m.id}`}>
                        {totalQty ? (
                          <span className={remaining > 0 ? "text-orange-600 font-medium" : "text-green-600 font-medium"}>
                            {remaining > 0 ? `${remaining} ${m.unit}` : "Completo"}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-sm">{m.min_stock} {m.unit}</TableCell>
                      <TableCell>
                        <Badge className={isLow ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                          {isLow ? "Baixo" : "OK"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {can("estoque", "edit") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => openMovement(m, "entrada")} title="Entrada" data-testid={`material-entry-${m.id}`}>
                            <ArrowDownCircle className="h-3.5 w-3.5" />
                          </Button>
                          )}
                          {can("estoque", "edit") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-600" onClick={() => openMovement(m, "saida")} title="Saida" data-testid={`material-exit-${m.id}`}>
                            <ArrowUpCircle className="h-3.5 w-3.5" />
                          </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openHistory(m)} title="Historico" data-testid={`material-history-${m.id}`}>
                            <History className="h-3.5 w-3.5" />
                          </Button>
                          {can("estoque", "edit") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)} data-testid={`material-edit-${m.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          )}
                          {can("estoque", "create") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDuplicate(m)} title="Duplicar" data-testid={`material-duplicate-${m.id}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          )}
                          {can("estoque", "delete") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(m.id)} data-testid={`material-delete-${m.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )})()}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Material Dialog */}
      <Dialog open={matDialog} onOpenChange={setMatDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Material" : "Novo Material"}</DialogTitle>
            <DialogDescription>Dados do material</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="material-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Codigo</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Ex: MAT-001" data-testid="material-code-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="material-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="material-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cliente (proprietario do material)</Label>
              <Select value={form.client_id || "_none"} onValueChange={handleClientChange}>
                <SelectTrigger data-testid="material-client-select"><SelectValue placeholder="Sem cliente (estoque proprio)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sem cliente (estoque proprio)</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Obra / Projeto</Label>
              <Input value={form.obra} onChange={(e) => setForm({ ...form, obra: e.target.value })} placeholder="Ex: Edificio Aurora - Bloco A" data-testid="material-obra-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{editing ? "Qtd. Recebida" : "Qtd. Recebida (inicial)"}</Label>
                <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} data-testid="material-quantity-input" />
              </div>
              <div className="space-y-2">
                <Label>Qtd. Total</Label>
                <Input type="number" step="0.01" value={form.total_quantity} onChange={(e) => setForm({ ...form, total_quantity: parseFloat(e.target.value) || 0 })} placeholder="Total que o cliente deve enviar" data-testid="material-total-quantity-input" />
              </div>
              <div className="space-y-2">
                <Label>Estoque Minimo</Label>
                <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })} data-testid="material-min-stock-input" />
              </div>
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label>Numero da NF (recebimento inicial)</Label>
                <Input value={form.nf} onChange={(e) => setForm({ ...form, nf: e.target.value })} placeholder="Ex: 12345" data-testid="material-nf-input" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMatDialog(false)}>Cancelar</Button>
              <Button type="submit" data-testid="material-submit-btn">{editing ? "Salvar" : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movDialog} onOpenChange={setMovDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{movForm.type === "entrada" ? "Entrada de Material" : "Saida de Material"}</DialogTitle>
            <DialogDescription>{selectedMat?.name} - Disponivel: {(selectedMat?.quantity || 0) - (selectedMat?.used_quantity || 0)} {selectedMat?.unit}{selectedMat?.client_name ? ` | Cliente: ${selectedMat.client_name}` : ""}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMovement} className="space-y-4">
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input type="number" step="0.01" min="0.01" value={movForm.quantity} onChange={(e) => setMovForm({ ...movForm, quantity: parseFloat(e.target.value) || 0 })} required data-testid="movement-quantity-input" />
            </div>
            <div className="space-y-2">
              <Label>Obra / Projeto</Label>
              <Input value={movForm.obra} onChange={(e) => setMovForm({ ...movForm, obra: e.target.value })} placeholder="Para qual obra e essa movimentacao?" data-testid="movement-obra-input" />
            </div>
            {movForm.type === "entrada" && (
              <div className="space-y-2">
                <Label>Numero da NF</Label>
                <Input value={movForm.nf} onChange={(e) => setMovForm({ ...movForm, nf: e.target.value })} placeholder="Ex: 12345" data-testid="movement-nf-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Observacao</Label>
              <Textarea value={movForm.notes} onChange={(e) => setMovForm({ ...movForm, notes: e.target.value })} rows={2} data-testid="movement-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMovDialog(false)}>Cancelar</Button>
              <Button type="submit" data-testid="movement-submit-btn">{movForm.type === "entrada" ? "Registrar Entrada" : "Registrar Saida"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={histDialog} onOpenChange={setHistDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Historico - {selectedMat?.name}</DialogTitle>
            <DialogDescription>Movimentacoes do material{selectedMat?.client_name ? ` | Cliente: ${selectedMat.client_name}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {movements.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhuma movimentacao</p>
            ) : (
              movements.map((mv) => (
                <div key={mv.id} className="p-2 rounded border border-border text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {mv.type === "entrada" ? (
                        <ArrowDownCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowUpCircle className="h-4 w-4 text-orange-600" />
                      )}
                      <span className="font-medium">{mv.type === "entrada" ? "+" : "-"}{mv.quantity}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{mv.created_by} - {new Date(mv.created_at).toLocaleDateString("pt-BR")}</span>
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground pl-6 flex-wrap">
                    {mv.obra && <span>Obra: <strong className="text-foreground">{mv.obra}</strong></span>}
                    {mv.nf && <span>NF: <strong className="text-foreground">{mv.nf}</strong></span>}
                    {mv.nf_date && <span>Data NF: <strong className="text-foreground">{new Date(mv.nf_date + "T00:00:00").toLocaleDateString("pt-BR")}</strong></span>}
                    {mv.notes && <span>{mv.notes}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <NFImportDialog open={nfImportOpen} onOpenChange={setNfImportOpen} clients={clients} onImported={fetchMaterials} />
    </div>
  );
}
